import { useCanViewEmails } from '../../../../helpers/useCanViewEmails';
import React, { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { enqueueSnackbar } from 'notistack';
import { saveAs } from 'file-saver';

import Modal from '../../Modal';
import { ContainedButton, OutlinedButton } from '../../Buttons';
import {
  createVideofeedExternalShare,
  getVideofeedExternalShareEmails,
  downloadVideofeedExternalShareEmailsJson,
  downloadVideofeedExternalShareEmailsPlist,
  getVideofeedDownloadDataByVideoFeedId,
  getVideofeedExternalShares,
  getPublicDownloadLinkExpiry,
} from '../../../../api';
import { useAppSelector } from '../../../../store/hooks';
import { VIDEOFEED, VIDEOFEED_TYPE } from '../../../../enums';
import styles from './ShareExternallyModal.module.scss';

type TShareExternallyModalProps = {
  isOpen: boolean;
  switchModal: () => void;
  feedId: string;
  referenceTimelines?: VIDEOFEED[];
};

type TExternalShareHistoryItem = {
  id: number;
  publicUrl: string;
  recipientEmails: string[];
  selectedVideos: Array<{ id: number; name: string }>;
  includeJson: boolean;
  expiresAt: string;
  createdAt: string;
  createdBy: { id: number; username?: string; email?: string } | null;
  message?: string | null;
  status: 'ACTIVE' | 'EXPIRED';
};

const ShareExternallyModal: React.FC<TShareExternallyModalProps> = ({
  isOpen,
  switchModal,
  feedId,
  referenceTimelines = [],
}) => {
  const canViewEmails = useCanViewEmails();
  const notification = useSnackbar();
  const { currentUser } = useAppSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [feed, setFeed] = useState<VIDEOFEED | null>(null);
  const [emails, setEmails] = useState('');
  const [message, setMessage] = useState('');
  const [includeJson, setIncludeJson] = useState(false);
  const [sendToSelf, setSendToSelf] = useState(false);
  const [selectedVideoIds, setSelectedVideoIds] = useState<number[]>([]);
  const [selectedJsonVideofeedIds, setSelectedJsonVideofeedIds] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<'create' | 'history'>('create');
  const [history, setHistory] = useState<TExternalShareHistoryItem[]>([]);
  const [globalEmailSuggestions, setGlobalEmailSuggestions] = useState<string[]>([]);
  const [ttlSeconds, setTtlSeconds] = useState(7 * 24 * 60 * 60);

  const collectionJsonTargets = useMemo(
    () =>
      (referenceTimelines || []).filter(
        (timeline) => timeline?.type === VIDEOFEED_TYPE.TIMELINE,
      ),
    [referenceTimelines],
  );
  const isCollectionJsonSelection = collectionJsonTargets.length > 1;
  const canShareJson =
    isCollectionJsonSelection ||
    (feed?.type === VIDEOFEED_TYPE.TIMELINE &&
      (!!feed?.uuid || !!feed?.topics?.length)) ||
    feed?.type === VIDEOFEED_TYPE.COMPOSER;

  const formatExpiry = (value: string) => {
    const date = new Date(value);
    if (date.getUTCFullYear() >= 9999) {
      return 'Never';
    }
    return date.toLocaleString();
  };

  const onClose = () => {
    if (loading) return;
    switchModal();
  };

  const loadFeed = async () => {
    try {
      setLoading(true);
      const [feedRes, historyRes, emailRes, expiryRes] = await Promise.all([
        getVideofeedDownloadDataByVideoFeedId(feedId),
        getVideofeedExternalShares(feedId),
        getVideofeedExternalShareEmails().catch(() => ({ data: [] })),
        getPublicDownloadLinkExpiry().catch(() => ({ ttlSeconds: 7 * 24 * 60 * 60 })),
      ]);
      const nextFeed = (feedRes?.data || null) as VIDEOFEED | null;
      setFeed(nextFeed);
      setSelectedVideoIds((nextFeed?.videos || []).map((video) => Number(video.id)));
      setHistory((historyRes?.data || []) as TExternalShareHistoryItem[]);
      setGlobalEmailSuggestions(
        ((emailRes?.data || []) as string[])
          .map((email) => String(email).toLowerCase().trim())
          .filter(Boolean),
      );
      setTtlSeconds(Number(expiryRes?.ttlSeconds) || 7 * 24 * 60 * 60);
    } catch (error) {
      console.error(error);
      notification.showMessage('Failed to load share data');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      setLoading(true);
      const res = await getVideofeedExternalShares(feedId);
      setHistory((res?.data || []) as TExternalShareHistoryItem[]);
    } catch (error) {
      console.error(error);
      notification.showMessage('Failed to load external shares');
    } finally {
      setLoading(false);
    }
  };

  const normalizedEmails = useMemo(() => {
    const entered = emails
      .split(/[\n,;]/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (sendToSelf && currentUser?.email) {
      entered.push(currentUser.email);
    }

    return Array.from(new Set(entered.map((email) => email.toLowerCase())));
  }, [currentUser?.email, emails, sendToSelf]);
  const emailSuggestions = useMemo(
    () =>
      Array.from(
        new Set([
          ...globalEmailSuggestions,
          ...history
            .flatMap((item) =>
              item.recipientEmails.map((email) => email.toLowerCase().trim()),
            )
            .filter(Boolean),
        ]),
      ),
    [globalEmailSuggestions, history],
  );

  const toggleVideo = (videoId: number) => {
    setSelectedVideoIds((prev) =>
      prev.includes(videoId)
        ? prev.filter((id) => id !== videoId)
        : [...prev, videoId],
    );
  };

  const toggleJsonVideofeed = (videofeedId: number) => {
    setSelectedJsonVideofeedIds((prev) =>
      prev.includes(videofeedId)
        ? prev.filter((id) => id !== videofeedId)
        : [...prev, videofeedId],
    );
  };

  const onSubmit = async () => {
    try {
      if (!normalizedEmails.length) {
        notification.showMessage('Enter at least one email');
        return;
      }

      if (!selectedVideoIds.length && !selectedJsonVideofeedIds.length && !includeJson) {
        notification.showMessage('Select at least one asset');
        return;
      }

      setLoading(true);
      const response = await createVideofeedExternalShare(feedId, {
        recipientEmails: normalizedEmails,
        selectedVideoIds,
        includeJson,
        selectedJsonVideofeedIds,
        message: message.trim() || undefined,
        ttlSeconds,
      });

      enqueueSnackbar(
        response?.data?.publicUrl ? 'External share created' : 'Share sent',
        { variant: 'success' },
      );
      switchModal();
    } catch (error: any) {
      notification.showMessage(
        error?.response?.data?.message || 'Failed to create external share',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setViewMode('create');
      setEmails('');
      setMessage('');
      setIncludeJson(false);
      setSelectedJsonVideofeedIds(
        isCollectionJsonSelection ? [] : [],
      );
      setSendToSelf(false);
      setHistory([]);
      setGlobalEmailSuggestions([]);
      loadFeed();
    }
  }, [isOpen]);

  const downloadBlob = async (
    loader: () => Promise<Blob>,
    filename: string,
  ) => {
    try {
      setLoading(true);
      const blob = await loader();
      saveAs(blob, filename);
    } catch (error: any) {
      notification.showMessage(
        error?.message || 'Failed to download external share emails',
      );
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar('External share link copied', { variant: 'success' });
    } catch {
      notification.showMessage('Failed to copy link');
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} loading={loading} mobileFullScreen>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>
          <div className={styles.titleText}>
            {viewMode === 'create' ? 'Share Externally' : 'External Shares'}
          </div>
        </div>
        {viewMode === 'create' ? (
          <div className={styles.form}>
          <div className={styles.section}>
            <label className={styles.fieldLabel}>Email addresses</label>
            <input
              list="external-share-emails"
              autoComplete="off"
              className={styles.textarea}
              value={emails}
              onChange={(event) => setEmails(event.target.value)}
              placeholder="Enter emails separated by commas"
            />
            {canViewEmails && <datalist id="external-share-emails">{emailSuggestions.map((email) => <option key={email} value={email} />)}</datalist>}
            <div className={styles.helperText}>Use commas to separate addresses.</div>
          </div>

          {false && (
            <div className={styles.secondaryBtnRow}>
              <button
                className={styles.linkButton}
                type="button"
                onClick={async () => {
                  await loadHistory();
                  setViewMode('history');
                }}
              >
                View External Shares
              </button>
            </div>
          )}

          <div className={styles.section}>
            <label className={styles.fieldLabel}>Angles</label>
            <div className={styles.assetsBox}>
              <div className={styles.checkboxGroup}>
                {(feed?.videos || []).map((video) => (
                  <label className={styles.checkboxRow} key={video.id}>
                    <input
                      type="checkbox"
                      checked={selectedVideoIds.includes(Number(video.id))}
                      onChange={() => toggleVideo(Number(video.id))}
                    />
                    <span className={styles.videoName}>{video.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {canShareJson && (
            <div className={styles.section}>
              <label className={styles.fieldLabel}>JSON</label>
              <div className={styles.assetsBox}>
                <div className={styles.checkboxGroup}>
                  {isCollectionJsonSelection ? (
                    collectionJsonTargets.map((timeline, index) => (
                      <label className={styles.checkboxRow} key={timeline.id}>
                        <input
                          type="checkbox"
                          checked={selectedJsonVideofeedIds.includes(Number(timeline.id))}
                          onChange={() => toggleJsonVideofeed(Number(timeline.id))}
                        />
                        <span>{timeline.title || `Timeline ${index + 1}`}</span>
                      </label>
                    ))
                  ) : (
                    <label className={styles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={includeJson}
                        onChange={() => {
                          const nextValue = !includeJson;
                          setIncludeJson(nextValue);
                          setSelectedJsonVideofeedIds(
                            nextValue && feed?.id ? [Number(feed.id)] : [],
                          );
                        }}
                      />
                      <span>Include JSON</span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={styles.section}>
            <label className={styles.fieldLabel}>Link expiry</label>
            <select
              className={styles.select}
              value={ttlSeconds}
              onChange={(event) => setTtlSeconds(Number(event.target.value))}
            >
              <option value={24 * 60 * 60}>24 hours</option>
              <option value={7 * 24 * 60 * 60}>7 days</option>
              <option value={30 * 24 * 60 * 60}>30 days</option>
            </select>
            <div className={styles.helperText}>
              Applies only to this sharing session.
            </div>
          </div>

          <div className={styles.section}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={sendToSelf}
                onChange={() => setSendToSelf((prev) => !prev)}
              />
              <span>Send to self</span>
            </label>
          </div>

          <div className={styles.section}>
            <label className={styles.fieldLabel}>Message</label>
            <textarea
              className={styles.message}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Optional note"
            />
          </div>

          <div className={styles.actionBtns}>
            <ContainedButton disabled={loading} onClick={onSubmit}>
              <span>Send</span>
            </ContainedButton>
            <OutlinedButton disabled={loading} kind="default" onClick={onClose}>
              <span>Cancel</span>
            </OutlinedButton>
          </div>
          </div>
        ) : (
          <div className={styles.form}>
            <div className={styles.secondaryBtnRow}>
              <button
                className={styles.linkButton}
                type="button"
                onClick={() => setViewMode('create')}
              >
                Back to Share
              </button>
            </div>

            {canViewEmails && <div className={styles.section}>
              <label className={styles.fieldLabel}>Export unique emails</label>
              <div className={styles.historyActions}>
                <ContainedButton
                  disabled={loading}
                  onClick={() =>
                    downloadBlob(
                      () => downloadVideofeedExternalShareEmailsJson(feedId),
                      'external-share-emails.json',
                    )
                  }
                >
                  <span>Download JSON</span>
                </ContainedButton>
                <OutlinedButton
                  disabled={loading}
                  kind="default"
                  onClick={() =>
                    downloadBlob(
                      () => downloadVideofeedExternalShareEmailsPlist(feedId),
                      'external-share-emails.plist',
                    )
                  }
                >
                  <span>Download plist</span>
                </OutlinedButton>
              </div>
            </div>}

            <div className={styles.historyList}>
              {!history.length && (
                <div className={styles.emptyState}>No external shares yet.</div>
              )}
              {history.map((item) => (
                <div className={styles.historyCard} key={item.id}>
                  <div className={styles.historyRow}>
                    <span className={styles.historyLabel}>Created:</span>
                    <span>{new Date(item.createdAt).toLocaleString()}</span>
                  </div>
                  <div className={styles.historyRow}>
                    <span className={styles.historyLabel}>By:</span>
                    <span>{item.createdBy?.username || (canViewEmails ? item.createdBy?.email : 'User') || 'Unknown'}</span>
                  </div>
                  <div className={styles.historyRow}>
                    <span className={styles.historyLabel}>Status:</span>
                    <span>{item.status}</span>
                  </div>
                  <div className={styles.historyRow}>
                    <span className={styles.historyLabel}>Expires:</span>
                    <span>{formatExpiry(item.expiresAt)}</span>
                  </div>
                  {canViewEmails && <div className={styles.historyRow}>
                    <span className={styles.historyLabel}>Emails:</span>
                    <span>{item.recipientEmails.join(', ') || '—'}</span>
                  </div>}
                  <div className={styles.historyRow}>
                    <span className={styles.historyLabel}>Angles:</span>
                    <span>{item.selectedVideos.map((video) => video.name).join(', ') || '—'}</span>
                  </div>
                  <div className={styles.historyRow}>
                    <span className={styles.historyLabel}>JSON:</span>
                    <span>{item.includeJson ? 'Yes' : 'No'}</span>
                  </div>
                  {!!item.message && (
                    <div className={styles.historyRow}>
                      <span className={styles.historyLabel}>Message:</span>
                      <span>{item.message}</span>
                    </div>
                  )}
                  <div className={styles.historyActions}>
                    <button
                      className={styles.linkButton}
                      type="button"
                      onClick={() => copyLink(item.publicUrl)}
                    >
                      Copy link
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.actionBtns}>
              <OutlinedButton disabled={loading} kind="default" onClick={onClose}>
                <span>Close</span>
              </OutlinedButton>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ShareExternallyModal;
